
import Vue from "vue";
import Component, { mixins } from "vue-class-component";
import { Watch } from "vue-property-decorator";
import { PingStatusMixin } from "@/helpers/PingStatusMixin";
import { namespace } from "vuex-class";
import { IShortLocker } from "@/model/shortInterfaces";
import { IAuthUserData } from "@/store/modules/authModule";
import { PermissionHelper } from "@/helpers/PermissionHelper";
import { BoListPanel, BoPanel, IOnOffHandlers } from "bonomo/dist";
import {ELockerStatuses, ILocker} from "@/model/Locker/lockerInterfaces";
import { BetterDataTableHeader } from "@/model/betterVuetifyInterfaces";
import LockerChip from "@/components/shared/chips/LockerChip.vue";
import LockerStatusChip from "@/components/shared/chips/LockerStatusChip.vue";
import PaginationBar from "@/components/shared/PaginationBar.vue";
import CsvDownloader from "@/components/shared/CsvDownloader.vue";
import { lockerService } from "@/services/lockerService";

const lockerModule = namespace("lockerModule");
const authModule = namespace("authModule");

@Component({
  components: {
    BoListPanel,
    BoPanel,
    LockerChip,
    LockerStatusChip,
    PaginationBar,
    CsvDownloader,
  },
})
export default class LockerList extends mixins(PingStatusMixin) {
  @lockerModule.Getter("lockersList")
  private lockersList!: IShortLocker[];

  @lockerModule.Getter("lockersListCurrentPage")
  private lockersListCurrentPage!: number;

  @lockerModule.Getter("lockersListLastPage")
  private lockersListLastPage!: number;

  @lockerModule.Getter("lockersListTotal")
  private lockersListTotal!: number;

  @lockerModule.Action("fetchLockersList")
  private fetchLockersList!: (filters: any) => any;

  @lockerModule.Action("clearLockersList")
  private clearLockersList!: () => void;

  @authModule.Getter("userData")
  private userData!: IAuthUserData;

  get isAdmin(): boolean {
    const role: any = this.userData && this.userData.role;
    return PermissionHelper.hasRoleAdministrator(this.userData) || role === 10 || role === '10';
  }

  get headers(): BetterDataTableHeader[] {
    return [
      {
        text: this.$t("common.name"),
        align: "start",
        value: "name",
      },
      { text: this.$t('common.community'), align: 'start', value: 'community' },

      {
        text: this.$t("lockers.lockers.fields.version"),
        align: "start",
        value: "version",
      },
      {
        text: this.$t("common.status"),
        align: "start",
        value: "status",
      },
      {
        text: this.$t("lockers.lockers.fields.last_sync"),
        align: "start",
        value: "last_ping",
      },
      { text: "", value: "detailButton" },
    ]
      .filter(each => !!each.value);
  }
  showDeleted: boolean = false;
  search: string = "";
  loading: boolean = true;
  private selectedPerPage: number = 15;
  readonly perPageOptions: number[] = [15, 20, 30, 50, 100];
  private searchTimer: any = null;
  private pollInterval: any = null;
  subscribes: IOnOffHandlers = {};

  created() {
    this.subscribes = this.$ons({
      'backend.call.OK': () => { this.loading = false; },
    });
    this.pollInterval = setInterval(this.silentFetch, 60000);
  }

  beforeDestroy() {
    this.$offs(this.subscribes);
    clearInterval(this.pollInterval);
  }

  loadData() {
    this.loading = true;
    this.clearLockersList();
    this.fetchLockersList(this.buildFilters());
  }

  private buildFilters(): any {
    const filters: any = { page: 1, per_page: this.selectedPerPage };
    if (this.search) filters.search = this.search;
    if (this.showDeleted) filters.show_deleted = 1;
    return filters;
  }

  private reload() {
    this.loading = true;
    this.clearLockersList();
    this.fetchLockersList(this.buildFilters());
  }

  onPageChange(page: number) {
    this.loading = true;
    this.clearLockersList();
    const filters = this.buildFilters();
    filters.page = page;
    this.fetchLockersList(filters);
  }

  onPerPageChange(value: number) {
    this.selectedPerPage = value;
    this.reload();
  }

  @Watch('search')
  onSearchChange() {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.reload();
    }, 400);
  }

  @Watch('showDeleted')
  onShowDeletedChange() {
    this.reload();
  }

  get csvHeaders(): string[] {
    return ['id', 'name', 'community', 'version', 'status', 'active', 'last_ping'];
  }

  get csvFileName(): string {
    return 'lockers_export.csv';
  }

  csvMapRow(item: IShortLocker): Array<string | number | null | undefined> {
    return [
      item.id,
      item.name || '',
      item.community || '',
      item.version || '',
      item.status || '',
      item.active ? '1' : '0',
      item.last_ping || '',
    ];
  }

  fetchLockersCsv(): Promise<any[]> {
    const filters: any = {};
    if (this.search) filters.search = this.search;
    if (this.showDeleted) filters.show_deleted = 1;
    return lockerService.exportLockers(filters);
  }

  shortDate(d: string) {
    return d ? d.slice(2, -3) : null;
  }

  private silentFetch() {
    const filters = this.buildFilters();
    filters.page = this.lockersListCurrentPage;
    this.fetchLockersList(filters);
  }

  get maxVersion(): string {
    let maxVersion = "";
    this.lockersList.forEach((each: IShortLocker) => {
      if (this.isNewerVersion(each.version.split("."), maxVersion.split("."))) {
        maxVersion = each.version;
      }
    });
    return maxVersion;
  }
  isMaxVersion(item: IShortLocker): boolean {
    return item.version == this.maxVersion;
  }
  versionColor(item: IShortLocker): string {
    if (this.isMaxVersion(item)) {
      return "green";
    } else if (item.status == ELockerStatuses.Online) {
      return "red";
    } else {
      return "orange";
    }
  }
  isNewerVersion(a: string[], b: string[]): boolean {
    if (!a.length) {
      return false;
    } else if (!b.length) {
      return true;
    } else if (Number(a[0]) > Number(b[0])) {
      return true;
    } else if (Number(b[0]) > Number(a[0])) {
      return false;
    }
    return this.isNewerVersion(a.slice(1), b.slice(1));
  }
  statusClicked(item: any) {
    this.search = item.status;
  }

  truncatedItem(item: IShortLocker): IShortLocker {
    return Object.assign({}, item, { name: item.name.slice(0, 47) + '...' });
  }

  copyText(text: string): void {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
    } else {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
  }
}
